import {
    CalendarEventParentId,
    GoalMetricItemLink,
    GoalMetricItemLinkKind,
    ReferenceBusinessId,
    TaskParentId,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { goalMetricItemHref } from "components/marketing-goals/goal-metric-item";
import { trpc } from "components/rpc/trpc";
import { TaskFormV2 } from "components/tasks/v2/task-form-v2";
import { Taskv2 } from "components/tasks/v2/types";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { CalendarEventDto } from "components/visits/types";
import { VisitEditor } from "components/visits/visit-editor";
import {
    AdvancedSearchEntityType,
    GetTasksForEntityQuery,
    useDeleteTaskMutation,
    useGetTasksForEntityQuery,
    useUpdateTaskMutation,
} from "generated/graphql";
import React from "react";
import { MyActivityItem } from "./my-activity";
import { useAgendaRefresh } from "./use-agenda-refresh";

type TaskDto = GetTasksForEntityQuery["getTasksForEntity"][0];

const entityTypeForLinkKind: Record<GoalMetricItemLinkKind, AdvancedSearchEntityType> = {
    placement: AdvancedSearchEntityType.Client,
    homeCare: AdvancedSearchEntityType.HomeCareLead,
    referenceContact: AdvancedSearchEntityType.ReferenceContact,
    referenceBusiness: AdvancedSearchEntityType.ReferenceBusiness,
    community: AdvancedSearchEntityType.Community,
};

const taskEntityForLink = (
    link: GoalMetricItemLink,
    parentName: string | null,
): Taskv2["entity"] => ({
    displayName: parentName,
    path: goalMetricItemHref(link),
    kind: entityTypeForLinkKind[link.kind],
    contact: null as Taskv2["entity"]["contact"],
});

export interface MyActivityEditing {
    onEditFor: (item: MyActivityItem) => (() => void) | null;
    editingTask: TaskDto | null;
    editingInitial: Taskv2 | null;
    editingVisit: CalendarEventDto | null;
    close: () => void;
}

export const useMyActivityEditing = (): MyActivityEditing => {
    const trpcClient = trpc.useTRPC();
    const [editing, setEditing] = React.useState<MyActivityItem | null>(null);
    const close = React.useCallback(() => setEditing(null), []);

    const editingLink = editing?.sourceId ? editing.link : null;
    const taskLink = editing?.kind === "task" ? editingLink : null;
    const visitLink = editing?.kind === "visit" ? editingLink : null;

    const tasksQuery = useGetTasksForEntityQuery(
        { parentId: (taskLink?.entityId ?? "") as TaskParentId },
        { enabled: !!taskLink },
    );
    const editingTask =
        editing && taskLink
            ? ((tasksQuery.data?.getTasksForEntity ?? []).find(
                  task => task.id === editing.sourceId,
              ) ?? null)
            : null;

    const visitsQueryOptions =
        visitLink?.kind === "referenceBusiness"
            ? trpcClient.calendarEvents.listByBusiness.queryOptions({
                  businessId: visitLink.entityId as ReferenceBusinessId,
              })
            : trpcClient.calendarEvents.listByParent.queryOptions({
                  parentId: (visitLink?.entityId ?? "") as CalendarEventParentId,
              });
    const visitsQuery = useQuery({ ...visitsQueryOptions, enabled: !!visitLink });
    const editingVisit: CalendarEventDto | null =
        editing && visitLink
            ? ((visitsQuery.data ?? []).find(visit => visit.id === editing.sourceId) ??
              null)
            : null;

    const editingInitial: Taskv2 | null =
        editingTask && editing?.link
            ? {
                  entity: taskEntityForLink(editing.link, editing.parentName),
                  task: editingTask,
              }
            : null;

    const onEditFor = React.useCallback(
        (item: MyActivityItem) =>
            (item.kind === "task" || item.kind === "visit") && item.sourceId && item.link
                ? () => setEditing(item)
                : null,
        [],
    );

    return { onEditFor, editingTask, editingInitial, editingVisit, close };
};

interface MyActivityEditingPanelsProps {
    editing: MyActivityEditing;
}

export const MyActivityEditingPanels: React.FC<MyActivityEditingPanelsProps> = ({
    editing,
}) => {
    const refreshAgenda = useAgendaRefresh();
    const updateTask = useUpdateTaskMutation();
    const deleteTask = useDeleteTaskMutation();
    const { editingTask, editingInitial, editingVisit, close } = editing;

    return (
        <>
            <ResponsivePanel
                isOpen={editingTask !== null}
                type={PanelType.medium}
                headerText="Edit task"
                onDismiss={close}>
                {editingInitial && editingTask ? (
                    <TaskFormV2
                        initial={editingInitial}
                        mode="update"
                        onClose={close}
                        saveTask={async (_parentId, payload) => {
                            await updateTask.mutateAsync({
                                taskId: editingTask.id,
                                params: payload,
                            });
                            close();
                            await refreshAgenda();
                        }}
                        onDelete={async () => {
                            await deleteTask.mutateAsync({ taskId: editingTask.id });
                            close();
                            await refreshAgenda();
                        }}
                        entityLinkDisabled
                    />
                ) : null}
            </ResponsivePanel>
            {editingVisit ? (
                <VisitEditor
                    mode="edit"
                    visit={editingVisit}
                    onSaved={() => {
                        close();
                        refreshAgenda();
                    }}
                    onClose={close}
                />
            ) : null}
        </>
    );
};
